/**
 * Filter helpers for the smart dashboard.
 *
 *  - canonical state shape is `{ dateRange:{from,to}, propertyIds:[], tenantIds:[], categories:[] }`
 *  - API expects `{ property_id: 'a,b,c', dateRange:{from,to} }` (and never client-supplied concessionaireId)
 *  - useWidgetData uses `stableFilterKey` to dedupe and avoid spurious refetches
 */

export function emptyState() {
    return {
        dateRange: null,
        propertyIds: [],
        tenantIds: [],
        categories: [],
    };
}

/**
 * Stable, order-independent fingerprint of the canonical filter state.
 * `{ propertyIds: [] }` and `{}` produce the same key.
 * Sorts arrays so element order doesn't trigger refetches.
 */
export function stableFilterKey(filters) {
    if (!filters) return '';
    const dr = filters.dateRange;
    const date = dr && dr.from && dr.to ? `${dr.from}|${dr.to}` : '';
    // useWidgetData receives the API-shape filters from toApiFilters (snake_case
    // CSV strings), not the canonical state. Support both so the fingerprint
    // changes whichever shape is passed in.
    const props = readListField(filters, 'propertyIds', 'property_id');
    const tenants = readListField(filters, 'tenantIds', 'tenant_id');
    const cats = readListField(filters, 'categories', 'category');
    // Per-instance widget config (e.g. dynamic_comparison entities) — when this
    // changes the widget must re-fetch even though dashboard filters are stable.
    const cfg = filters.config ? stableConfigKey(filters.config) : '';
    return [date, props, tenants, cats, cfg].join('::');
}

function readListField(filters, camelKey, snakeKey) {
    const cam = filters[camelKey];
    if (Array.isArray(cam) && cam.length) return cam.slice().sort().join(',');
    const snake = filters[snakeKey];
    if (typeof snake === 'string' && snake) {
        return snake.split(',').map(s => s.trim()).filter(Boolean).sort().join(',');
    }
    if (Array.isArray(snake) && snake.length) return snake.slice().sort().join(',');
    return '';
}

function stableConfigKey(cfg) {
    try { return JSON.stringify(sortKeysDeep(cfg)); }
    catch { return ''; }
}

/**
 * Recursively key-sorted copy, so the fingerprint is order-independent at every
 * depth.
 *
 * This used to be `JSON.stringify(cfg, Object.keys(cfg).sort())`. The second
 * argument to stringify is a replacer, and an array replacer is an allow-list
 * applied at EVERY level — not just the top. So a config of
 * `{ propertyId, dateRange: { from, to } }` serialised as
 * `{"dateRange":{},"propertyId":"..."}`: `from` and `to` were dropped because
 * they weren't also top-level keys. Changing the dates never moved the
 * fingerprint, so useWidgetData saw no change and never refetched.
 */
function sortKeysDeep(value) {
    if (Array.isArray(value)) return value.map(sortKeysDeep);
    if (value && typeof value === 'object') {
        return Object.keys(value).sort().reduce((acc, k) => {
            acc[k] = sortKeysDeep(value[k]);
            return acc;
        }, {});
    }
    return value;
}

/**
 * Translate canonical state -> API request body.
 * `propertyIds` becomes a comma-separated `property_id` string (matches existing
 * service contract — see deliveryPartnerService.parsePropertyIds).
 * Empty arrays are dropped so the backend resolves to "all accessible".
 */
export function toApiFilters(filters, fallbackPropertyAccess) {
    const out = {};
    if (filters.dateRange && filters.dateRange.from && filters.dateRange.to) {
        out.dateRange = { from: filters.dateRange.from, to: filters.dateRange.to };
        // Request prior-period comparison so widgets that display growth/delta
        // (sales, revenue, ATV, …) still get the comparison values for any
        // user-selected range. Services that don't accept this key ignore it.
        out.comparePrevious = true;
    }
    const ids = filters.propertyIds && filters.propertyIds.length
        ? filters.propertyIds
        : null;
    if (ids) {
        out.property_id = ids.join(',');
    } else if (fallbackPropertyAccess) {
        // Preserve existing behaviour: when "all properties" is selected we still
        // narrow to the user's accessible set (backend also enforces, but this
        // avoids surprises if the auth scope ever loosens).
        out.property_id = fallbackPropertyAccess;
    }
    return out;
}

/**
 * Canonical -> URL searchParams string fragment.
 * Empty arrays / nulls are omitted.
 */
export function toUrlParams(filters) {
    const usp = new URLSearchParams();
    if (filters.dateRange?.from) usp.set('from', filters.dateRange.from);
    if (filters.dateRange?.to) usp.set('to', filters.dateRange.to);
    if (filters.propertyIds?.length) usp.set('props', filters.propertyIds.join(','));
    if (filters.tenantIds?.length) usp.set('tenants', filters.tenantIds.join(','));
    if (filters.categories?.length) usp.set('cats', filters.categories.join(','));
    return usp.toString();
}

/**
 * URL searchParams -> canonical filters.
 */
export function fromUrlParams(searchString) {
    const usp = new URLSearchParams(searchString || '');
    const from = usp.get('from');
    const to = usp.get('to');
    return {
        dateRange: from && to ? { from, to } : null,
        propertyIds: splitCsv(usp.get('props')),
        tenantIds: splitCsv(usp.get('tenants')),
        categories: splitCsv(usp.get('cats')),
    };
}

function splitCsv(v) {
    if (!v) return [];
    return v.split(',').map(s => s.trim()).filter(Boolean);
}

export function localStorageKey(layoutKey) {
    return `v3.dashboard.filters.${layoutKey || 'default'}`;
}

export function isValidRange(range) {
    if (!range || !range.from || !range.to) return false;
    return range.from <= range.to;
}
